/**
 * 路由表（TanStack Router 代码式路由）。
 * 路径、标题、图标、排序、权限与旧版（Vue）管理中台的路由一一对应。
 */
import { useEffect } from 'react'
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useMatches,
  type AnyRoute,
  type RouteComponent,
  type RouterHistory
} from '@tanstack/react-router'
import {
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LineChartOutlined,
  RobotOutlined,
  SearchOutlined,
  SoundOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons'
import { setUnauthorizedHandler } from '@/api/client'
import { feedback } from '@/lib/feedback'
import { selectIsAuthenticated, useAuthStore } from '@/stores/auth'
import { AdminLayout } from '@/layouts/AdminLayout'
import { LoginPage } from '@/features/auth/LoginPage'
import { NotFoundPage } from '@/features/common/NotFoundPage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { ThemeListPage } from '@/features/themes/ThemeListPage'
import { ThemeDetailPage } from '@/features/themes/ThemeDetailPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { ProductKpiPage } from '@/features/analytics/ProductKpiPage'
import { AIProviderPage } from '@/features/system/ai-providers/AIProviderPage'
import { TokenUsagePage } from '@/features/system/token-usage/TokenUsagePage'
import { AuditLogsPage } from '@/features/system/audit-logs/AuditLogsPage'
import { AdminManagementPage } from '@/features/system/admin-management/AdminManagementPage'
import { TTSResourcePage } from '@/features/tts/TTSResourcePage'
import { DocumentManagementPage } from '@/features/documents/DocumentManagementPage'
import { StatsPage as LexemeStatsPage } from '@/features/dynamic-lexemes/StatsPage'
import { PendingReviewPage } from '@/features/dynamic-lexemes/PendingReviewPage'
import { ApprovedPage as LexemeApprovedPage } from '@/features/dynamic-lexemes/ApprovedPage'
import { SearchPage as LexemeSearchPage } from '@/features/dynamic-lexemes/SearchPage'
import { UserListPage } from '@/features/users/UserListPage'
import { UserVocabularyPage } from '@/features/users/UserVocabularyPage'
import { UserLLMLogsPage } from '@/features/users/UserLLMLogsPage'
import { UserInstanceListPage } from '@/features/user-instances/UserInstanceListPage'
import { GlobalLLMLogsPage } from '@/features/llm-logs/GlobalLLMLogsPage'
import { HOME_PATH, pageTitle, type RouteMeta } from './guards'
import type { MenuSourceRoute } from './menu'

async function ensureInitialized() {
  await useAuthStore.getState().initialize()
  return selectIsAuthenticated(useAuthStore.getState())
}

// 根组件：按当前页面设置浏览器标题
function RootLayout() {
  const title = useMatches({ select: (matches) => matches[matches.length - 1]?.staticData.title })
  useEffect(() => {
    document.title = pageTitle(title)
  }, [title])
  return <Outlet />
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  staticData: { title: '登录' },
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined
  }),
  // 已登录访问登录页回到首页
  beforeLoad: async () => {
    if (await ensureInitialized()) throw redirect({ to: HOME_PATH })
  },
  component: LoginPage
})

// 主布局：未登录访问任何受保护页面都跳到登录页，并带上原目标
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  beforeLoad: async ({ location }) => {
    if (!(await ensureInitialized())) {
      let messageApi: typeof feedback.message | null = null
      try {
        messageApi = feedback.message
      } catch {
        // 首次加载时提示组件还没挂载，直接跳转
      }
      messageApi?.warning('请先登录')
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: AdminLayout
})

const indexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: HOME_PATH })
  }
})

// 每个页面一条路由
function page<TPath extends string>(path: TPath, staticData: RouteMeta, component: RouteComponent) {
  return createRoute({ getParentRoute: () => adminRoute, path, staticData, component })
}

const dashboardRoute = page('/dashboard', { title: '仪表板', icon: DashboardOutlined, order: 100 }, DashboardPage)

// ?search= 来自 Header 搜索框，作为用户列表的初始关键词
const userListRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  staticData: { title: '用户管理', icon: UserOutlined, order: 200, permissions: ['user:view'] },
  validateSearch: (search: Record<string, unknown>): { search?: string } => ({
    search: typeof search.search === 'string' && search.search ? search.search : undefined
  }),
  component: UserListPage
})
const userVocabularyRoute = page(
  '/users/$id/vocabulary',
  { title: '用户词汇详情', permissions: ['user:view'], hideInMenu: true },
  UserVocabularyPage
)
const userLLMLogsRoute = page(
  '/users/$id/llm-logs',
  { title: 'AI调用记录', permissions: ['user:view'], hideInMenu: true },
  UserLLMLogsPage
)

const ttsResourceRoute = page('/tts-resource', { title: 'TTS资源库', icon: SoundOutlined, order: 305 }, TTSResourcePage)

const productKpiRoute = page('/product-kpi', {
  title: '产品 KPI',
  icon: LineChartOutlined,
  permissions: ['analytics:product-kpi:view'],
  order: 502
}, ProductKpiPage)

const aiProvidersRoute = page('/ai-providers', {
  title: 'AI供应商',
  icon: RobotOutlined,
  permissions: ['system:ai:manage'],
  order: 401
}, AIProviderPage)
const tokenUsageRoute = page('/token-usage', {
  title: 'Token统计',
  icon: BarChartOutlined,
  permissions: ['system:token:view'],
  order: 501
}, TokenUsagePage)
// ?page= 记录当前页码，刷新后保持
const llmRequestLogsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/llm-request-logs',
  staticData: { title: 'AI调用记录', icon: AuditOutlined, permissions: ['system:logs:view'], order: 406 },
  validateSearch: (search: Record<string, unknown>): { page?: number } => {
    const pageNumber = Number(search.page)
    return { page: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : undefined }
  },
  component: GlobalLLMLogsPage
})
const auditLogsRoute = page('/audit-logs', {
  title: '操作审计',
  icon: AuditOutlined,
  permissions: ['system:audit:view'],
  order: 601
}, AuditLogsPage)
// 所有管理员都可查看，只有超管可操作
const adminManagementRoute = page('/admin-management', { title: '管理员管理', icon: TeamOutlined, order: 602 }, AdminManagementPage)

const dynamicLexemesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/dynamic-lexemes',
  staticData: { title: '动态词汇管理', icon: BookOutlined, order: 350 }
})
const dynamicLexemesIndexRoute = createRoute({
  getParentRoute: () => dynamicLexemesRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dynamic-lexemes/stats' })
  }
})
function lexemePage<TPath extends string>(path: TPath, staticData: RouteMeta, component: RouteComponent) {
  return createRoute({ getParentRoute: () => dynamicLexemesRoute, path, staticData, component })
}
const lexemeStatsRoute = lexemePage('stats', { title: '词汇统计', icon: BarChartOutlined }, LexemeStatsPage)
const lexemePendingRoute = lexemePage('pending-review', { title: '待审核词汇', icon: AuditOutlined }, PendingReviewPage)
const lexemeApprovedRoute = lexemePage('approved', { title: '已批准词汇', icon: CheckCircleOutlined }, LexemeApprovedPage)
const lexemeSearchRoute = lexemePage('search', { title: '词汇搜索', icon: SearchOutlined }, LexemeSearchPage)

const themesRoute = page(
  '/themes',
  { title: '主题管理', icon: FileTextOutlined, permissions: ['theme:view'], order: 301 },
  ThemeListPage
)
const themeDetailRoute = page(
  '/themes/$id',
  { title: '主题详情', permissions: ['theme:view'], hideInMenu: true },
  ThemeDetailPage
)
const userInstancesRoute = page(
  '/user-instances',
  { title: '用户实例管理', icon: TeamOutlined, permissions: ['user-instance:view'], order: 302 },
  UserInstanceListPage
)

const documentsRoute = page('/documents', {
  title: '文档管理',
  icon: FileTextOutlined,
  permissions: ['system:documents:manage'],
  order: 601
}, DocumentManagementPage)

const profileRoute = page('/profile', { title: '个人资料', icon: UserOutlined, order: 701 }, SettingsPage)

// 子路由顺序与旧版 router/index.ts 一致：同一 order 时按这里的先后排菜单
export const adminPageRoutes = [
  dashboardRoute,
  userListRoute,
  userVocabularyRoute,
  userLLMLogsRoute,
  ttsResourceRoute,
  productKpiRoute,
  aiProvidersRoute,
  tokenUsageRoute,
  llmRequestLogsRoute,
  auditLogsRoute,
  adminManagementRoute,
  dynamicLexemesRoute.addChildren([
    dynamicLexemesIndexRoute,
    lexemeStatsRoute,
    lexemePendingRoute,
    lexemeApprovedRoute,
    lexemeSearchRoute
  ]),
  themesRoute,
  themeDetailRoute,
  userInstancesRoute,
  documentsRoute,
  profileRoute
]

export const routeTree = rootRoute.addChildren([loginRoute, adminRoute.addChildren([indexRoute, ...adminPageRoutes])])

// 侧边栏菜单的数据来源；fullPath 在路由器创建后才算好，所以按需调用
function toMenuSource(route: AnyRoute): MenuSourceRoute {
  const children = (route.children as AnyRoute[] | undefined)?.filter((child) => child.options.staticData?.title)
  return { path: route.fullPath, meta: route.options.staticData, children: children?.map(toMenuSource) }
}

export function getMenuSources(): MenuSourceRoute[] {
  return adminPageRoutes.map((route) => toMenuSource(route as AnyRoute))
}

export function createAppRouter(history?: RouterHistory) {
  const router = createRouter({ routeTree, history })
  // 请求返回 401 时回到登录页，带上当前页面
  setUnauthorizedHandler(() => {
    // 已经在登录页时不再跳转，避免 redirect 指回登录页
    if (router.state.location.pathname === '/login') return
    router.navigate({ to: '/login', search: { redirect: router.state.location.href } }).catch(console.error)
  })
  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
